import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const RoleTree = ({ 
  roles, 
  selectedRole, 
  searchTerm, 
  onRoleSelect, 
  onDeleteRole 
}) => {
  const [draggedRole, setDraggedRole] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Filter roles based on search term
  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Build role hierarchy
  const buildRoleHierarchy = (roles) => {
    const roleMap = {};
    const rootRoles = [];

    // Create role map
    roles.forEach(role => {
      roleMap[role.id] = { ...role, children: [] };
    });

    // Build hierarchy
    roles.forEach(role => {
      if (role.parentId && roleMap[role.parentId]) {
        roleMap[role.parentId].children.push(roleMap[role.id]);
      } else {
        rootRoles.push(roleMap[role.id]);
      }
    });

    return rootRoles;
  };

  const roleHierarchy = buildRoleHierarchy(filteredRoles);

  const handleDragStart = (e, role) => {
    setDraggedRole(role);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, role) => {
    e.preventDefault();
    setDropTarget(role);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTarget(null);
    }
  };

  const handleDrop = (e, targetRole) => {
    e.preventDefault();
    setDropTarget(null);
    
    if (draggedRole && draggedRole.id !== targetRole.id) {
      // In real app, this would update the role hierarchy
      console.log('Moving role:', draggedRole.name, 'under:', targetRole.name);
    }
    
    setDraggedRole(null);
  };

  const getRoleIcon = (role) => {
    if (role.name.toLowerCase().includes('admin')) return 'Crown';
    if (role.name.toLowerCase().includes('manager')) return 'Users';
    if (role.name.toLowerCase().includes('lead')) return 'UserCheck';
    if (role.name.toLowerCase().includes('guest')) return 'UserMinus';
    return 'User';
  };

  const getRoleColor = (role) => {
    if (role.name.toLowerCase().includes('admin')) return 'text-error';
    if (role.name.toLowerCase().includes('manager')) return 'text-accent';
    if (role.name.toLowerCase().includes('lead')) return 'text-primary';
    if (role.name.toLowerCase().includes('guest')) return 'text-secondary-400';
    return 'text-secondary-500';
  };

  const renderRole = (role, level = 0) => {
    const isSelected = selectedRole?.id === role.id;
    const hasChildren = role.children && role.children.length > 0;
    const isDropTarget = dropTarget?.id === role.id;

    return (
      <div key={role.id} className="mb-1">
        <div
          draggable
          onDragStart={(e) => handleDragStart(e, role)}
          onDragOver={handleDragOver}
          onDragEnter={(e) => handleDragEnter(e, role)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, role)}
          className={`
            group relative flex items-center justify-between p-3 rounded-md cursor-pointer
            transition-all duration-150 ease-out hover-lift
            ${level > 0 ? 'ml-6 border-l-2 border-secondary-200' : ''}
            ${isSelected 
              ? 'bg-primary-50 border border-primary-200 text-primary-700' :'hover:bg-secondary-50'
            }
            ${isDropTarget ? 'bg-accent-50 border border-accent-200' : ''}
            ${draggedRole?.id === role.id ? 'opacity-50' : ''}
          `}
          onClick={() => onRoleSelect(role)}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`p-1.5 rounded-md bg-secondary-100 ${getRoleColor(role)}`}>
              <Icon name={getRoleIcon(role)} size={16} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-text-primary truncate">
                  {role.name}
                </h3>
                {role.parentId && (
                  <div className="w-1.5 h-1.5 bg-secondary-300 rounded-full"></div>
                )}
              </div>
              <p className="text-xs text-text-secondary truncate mt-0.5">
                {role.userCount} users assigned
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRole(role);
              }}
              className="p-1.5 rounded-md hover:bg-error-50 hover:text-error transition-colors duration-150"
              title="Delete role"
            >
              <Icon name="Trash2" size={14} />
            </button>
            
            <div className="w-4 h-4 flex items-center justify-center">
              <Icon name="GripVertical" size={12} className="text-secondary-400" />
            </div>
          </div>
        </div>

        {hasChildren && (
          <div className="mt-1">
            {role.children.map(child => renderRole(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (filteredRoles.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Icon name="Search" size={20} className="text-secondary-400" />
        </div>
        <p className="text-text-secondary">
          {searchTerm ? 'No roles found matching your search' : 'No roles available'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {roleHierarchy.map(role => renderRole(role))}
      
      {searchTerm && (
        <div className="mt-4 p-3 bg-secondary-50 rounded-md">
          <p className="text-sm text-text-secondary">
            Showing {filteredRoles.length} of {roles.length} roles
          </p>
        </div>
      )}
    </div>
  );
};

export default RoleTree;